import { useMemo } from 'react'
import {
  AddLibraryCollectionCollaboratorBody,
  LibraryCollectionCollaborator,
  LibraryCollectionSharing,
  LibraryCollectionSharingMode,
  RemoveLibraryCollectionCollaboratorResponse,
  UpdateLibraryCollectionSharingBody,
} from '@digital-twins/shared-types'

import api from '@/lib/api/client'
import { organizationsApi } from '@/lib/api'
import { ShareSettingsAdapter } from '@/components/sharing'

function sharingPath(collectionToken: string): string {
  return `/library/collections/${encodeURIComponent(collectionToken)}/sharing`
}

/**
 * The collection share API (shared-types `library-collections.ts`), shaped
 * like the audience one (`/populations/:id/sharing*`). Owner only.
 */
export const libraryCollectionSharingApi = {
  getSharing: async (collectionToken: string): Promise<LibraryCollectionSharing> => {
    const response = await api.get(sharingPath(collectionToken))
    return response.data
  },
  updateSharing: async (
    collectionToken: string,
    body: UpdateLibraryCollectionSharingBody,
  ): Promise<{ sharingMode: LibraryCollectionSharingMode }> => {
    const response = await api.patch(sharingPath(collectionToken), body)
    return response.data
  },
  addCollaborator: async (
    collectionToken: string,
    body: AddLibraryCollectionCollaboratorBody,
  ): Promise<LibraryCollectionCollaborator> => {
    const response = await api.post(`${sharingPath(collectionToken)}/collaborators`, body)
    return response.data
  },
  removeCollaborator: async (
    collectionToken: string,
    collaboratorId: string,
  ): Promise<RemoveLibraryCollectionCollaboratorResponse> => {
    const response = await api.delete(
      `${sharingPath(collectionToken)}/collaborators/${encodeURIComponent(collaboratorId)}`,
    )
    return response.data
  },
}

/** Collections offer no guests and no public link (D2). */
function unsupported(): never {
  throw new Error('Collections are shared with org members only')
}

/**
 * `ShareSettingsAdapter` for a library collection, modelled on
 * `useAudienceShareAdapter`: Only me (`private`), Specific people (`shared`
 * plus collaborators) and Whole org (`organization`). Null until both
 * tokens are known.
 */
export function useCollectionShareAdapter(
  collectionToken: string | null | undefined,
  organizationToken: string | null | undefined,
): ShareSettingsAdapter | null {
  return useMemo(() => {
    if (!collectionToken || !organizationToken) {
      return null
    }

    const adapter: ShareSettingsAdapter = {
      async loadSharingData() {
        const data = await libraryCollectionSharingApi.getSharing(collectionToken)
        return {
          sharingMode: data.sharingMode,
          collaborators: data.collaborators.map((collaborator) => ({
            id: collaborator.id,
            userId: collaborator.userId,
            role: collaborator.role,
            user: {
              name: collaborator.name,
              email: collaborator.email,
              avatarSeed: null,
            },
          })),
          guests: [],
        }
      },
      async loadShareStatus() {
        return { isPublic: false, shareUrl: null }
      },
      async loadOrgMembers() {
        return organizationsApi.getMembers(organizationToken)
      },
      async setSharingMode(mode) {
        await libraryCollectionSharingApi.updateSharing(collectionToken, { sharingMode: mode })
      },
      async addCollaborator(userId) {
        await libraryCollectionSharingApi.addCollaborator(collectionToken, { userId })
      },
      async removeCollaborator(id) {
        await libraryCollectionSharingApi.removeCollaborator(collectionToken, id)
      },
      async addGuest() {
        unsupported()
      },
      async removeGuest() {
        unsupported()
      },
      async enableSharing() {
        return unsupported()
      },
      async disableSharing() {
        unsupported()
      },
    }

    return adapter
  }, [collectionToken, organizationToken])
}
